/** Parties editor — manage MembershipParty rows (CEO / legal_rep /
 * syndic / etc.) on a single Membership.
 *
 * Beneficiary row is read-only (auto-spawned by signal on the backend;
 * deletion is rejected). The "Add party" form picks an existing Contact
 * by typeahead-ish email + role; creating new Contacts is left to the
 * /contacts/ surface so the operator can fill identity + CFs first.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import type {
  Contact,
  MembershipParty,
  MembershipPartyRole,
  Paginated,
} from "@/lib/types";

import { addMembershipParty, removeMembershipParty } from "./api";


const NON_BENEFICIARY_ROLES: MembershipPartyRole[] = [
  "ceo",
  "legal_rep",
  "syndic",
  "owner",
  "project_manager",
  "architect",
  "other",
];


export function MembershipPartiesEditor({
  campaignId,
  membershipId,
  initialParties,
}: {
  campaignId: string;
  membershipId: string;
  initialParties: MembershipParty[];
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ["membership-parties", campaignId, membershipId],
    queryFn: async () => {
      const res = await api.get<MembershipParty[]>(
        ApiPaths.campaignMembershipParties(campaignId, membershipId),
      );
      return res.data;
    },
    initialData: initialParties,
  });

  const remove = useMutation({
    mutationFn: (partyId: string) =>
      removeMembershipParty(campaignId, membershipId, partyId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["membership-parties", campaignId, membershipId],
      });
      void qc.invalidateQueries({
        queryKey: ["campaign-leads", campaignId],
      });
    },
  });

  const parties = list.data ?? [];

  return (
    <div className="space-y-3 rounded-lg border border-nb-border bg-nb-bg/60 p-3 text-sm">
      <h4 className="text-[10px] font-semibold uppercase tracking-wide text-nb-text-3">
        {t("membershipParties.heading", "Parties")}
      </h4>
      <ul className="divide-y divide-nb-border/60">
        {parties.map((p) => (
          <li key={p.id} className="flex items-start justify-between gap-3 py-2 first:pt-0">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="shrink-0 whitespace-nowrap rounded bg-nb-surface px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-nb-text-2">
                  {t(`membershipParties.role_${p.role}`, p.role)}
                </span>
                <span className="truncate text-sm font-medium text-nb-text">
                  {p.contact_first_name} {p.contact_last_name}
                </span>
                {p.contact_is_business && (
                  <span className="shrink-0 whitespace-nowrap rounded bg-nb-mint/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-nb-mint">
                    {t("contacts.businessHeading", "Legal entity")}
                  </span>
                )}
              </div>
              {p.contact_email && (
                <p className="mt-0.5 truncate text-xs text-nb-text-3">
                  {p.contact_email}
                </p>
              )}
            </div>
            {p.role !== "beneficiary" && (
              <button
                type="button"
                className="shrink-0 text-xs text-cvp-danger hover:underline"
                onClick={() => {
                  if (
                    window.confirm(
                      t(
                        "membershipParties.removeConfirm",
                        "Remove this party from the membership?",
                      ),
                    )
                  ) {
                    remove.mutate(p.id);
                  }
                }}
              >
                {t("common.remove", "Remove")}
              </button>
            )}
          </li>
        ))}
      </ul>
      <AddPartyForm
        campaignId={campaignId}
        membershipId={membershipId}
        existingContactIds={new Set(parties.map((p) => p.contact))}
      />
    </div>
  );
}


function AddPartyForm({
  campaignId,
  membershipId,
  existingContactIds,
}: {
  campaignId: string;
  membershipId: string;
  existingContactIds: Set<string>;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MembershipPartyRole>("ceo");
  const [open, setOpen] = useState(false);

  // Look up Contact by email. Backend supports `?q=` search on the
  // contacts list endpoint.
  const lookup = useQuery({
    queryKey: ["contact-search", email],
    queryFn: async () => {
      if (!email.trim()) return [];
      const res = await api.get<Paginated<Contact>>(ApiPaths.contacts, {
        params: { q: email.trim() },
      });
      return res.data.results ?? [];
    },
    enabled: email.trim().length >= 3,
  });

  const create = useMutation({
    mutationFn: ({ contact_id }: { contact_id: string }) =>
      addMembershipParty(campaignId, membershipId, { contact_id, role }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["membership-parties", campaignId, membershipId],
      });
      void qc.invalidateQueries({
        queryKey: ["campaign-leads", campaignId],
      });
      setEmail("");
      setOpen(false);
    },
  });

  if (!open) {
    return (
      <button
        type="button"
        className="btn-ghost text-xs"
        onClick={() => setOpen(true)}
      >
        + {t("membershipParties.addButton", "Add party")}
      </button>
    );
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
  }

  return (
    <form onSubmit={onSubmit} className="space-y-2 border-t border-nb-border pt-2">
      <div className="grid grid-cols-2 gap-2">
        <label>
          <span className="block text-xs text-nb-text-2">
            {t("membershipParties.role", "Role")}
          </span>
          <select
            className="input mt-1 w-full text-xs"
            value={role}
            onChange={(e) => setRole(e.target.value as MembershipPartyRole)}
          >
            {NON_BENEFICIARY_ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`membershipParties.role_${r}`, r)}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="block text-xs text-nb-text-2">
            {t("membershipParties.searchEmail", "Search by email / name")}
          </span>
          <input
            type="text"
            className="input mt-1 w-full text-xs"
            value={email}
            placeholder="ceo@example.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
      </div>

      {lookup.data && lookup.data.length > 0 && (
        <ul className="max-h-32 space-y-1 overflow-y-auto rounded border border-nb-border bg-nb-surface/50 p-1.5">
          {lookup.data.map((c) => {
            const dup = existingContactIds.has(c.id);
            return (
              <li
                key={c.id}
                className="flex items-center justify-between gap-2 text-xs"
              >
                <span className="text-nb-text">
                  {c.first_name} {c.last_name}{" "}
                  <span className="text-nb-text-3">{c.email}</span>
                </span>
                <button
                  type="button"
                  className="btn-primary text-[10px]"
                  disabled={dup || create.isPending}
                  onClick={() => create.mutate({ contact_id: c.id })}
                >
                  {dup
                    ? t("membershipParties.alreadyParty", "already party")
                    : t("membershipParties.attach", "Attach")}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {lookup.data && lookup.data.length === 0 && email.trim().length >= 3 && (
        <p className="text-[10px] text-nb-text-3">
          {t(
            "membershipParties.noMatch",
            "No matching contact — create them from the Beneficiaries page first.",
          )}
        </p>
      )}

      {create.isError && (
        <p className="text-xs text-cvp-danger">{String(create.error)}</p>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => setOpen(false)}
        >
          {t("common.cancel", "Cancel")}
        </button>
      </div>
    </form>
  );
}
